import { apiClient } from '@/hooks/useApi';

export type StaffRole = 'Owner' | 'Manager' | 'Technician' | 'SeniorTechnician' | 'ServiceAdvisor' | 'PartClerk' | 'Receptionist' | 'Apprentice' | 'Student' | 'Intern';

export interface StaffMember {
  staffId: number;
  staffNumber?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  role: StaffRole;
  isActive: boolean;
  canLogin: boolean;
  username?: string;
  hireDate: string;
  hourlyRate?: number;
  jobLimit?: number | null;
}

export interface SetPasswordRequest {
  newPassword: string;
}

export interface GeneratePinResponse {
  pin: string;
}

export interface TechnicianWorkload {
  staffId: number;
  name: string;
  role: string;
  activeJobCount: number;
  limit: number;
  capacityStatus: 'available' | 'atLimit' | 'overLimit';
}

export const staffApi = {
  async list(): Promise<StaffMember[]> {
    const { data } = await apiClient.get<StaffMember[]>('/api/staff');
    return data;
  },

  async get(id: number): Promise<StaffMember> {
    const { data } = await apiClient.get<StaffMember>(`/api/staff/${id}`);
    return data;
  },

  async create(payload: Omit<StaffMember, 'staffId'>): Promise<StaffMember> {
    const { data } = await apiClient.post<StaffMember>('/api/staff', payload);
    return data;
  },

  async update(id: number, payload: Partial<StaffMember>): Promise<StaffMember> {
    const { data } = await apiClient.put<StaffMember>(`/api/staff/${id}`, payload);
    return data;
  },

  async deactivate(id: number): Promise<void> {
    await apiClient.post(`/api/staff/${id}/deactivate`);
  },

  async activate(id: number): Promise<void> {
    await apiClient.post(`/api/staff/${id}/activate`);
  },

  async setPassword(id: number, payload: SetPasswordRequest): Promise<void> {
    await apiClient.post(`/api/staff/${id}/password`, payload);
  },

  async generateTechnicianPin(id: number): Promise<GeneratePinResponse> {
    const { data } = await apiClient.post<GeneratePinResponse>(`/api/staff/${id}/technicianpin`);
    return data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/api/staff/change-password', {
      currentPassword,
      newPassword,
    });
  },

  async getWorkloads(): Promise<TechnicianWorkload[]> {
    const { data } = await apiClient.get<TechnicianWorkload[]>('/api/staff/workload');
    return data;
  },
};
